import { useTranslation } from 'react-i18next';
import { useUser } from 'app/state/contexts/UserContext';
import { useDocumentParams } from 'app/router/params';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';

import { ToolMenu } from 'app/components';
import Tippy from '@tippyjs/react';
import { LazyTippy } from 'app/components';
import { i18nDynamic } from 'shared/i18n';
import Modal from 'shared/overlays/Modal';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { useDocumentPermissions, useAddDocumentPermissionUser, useRemoveDocumentPermissionUser, permissionGroupKeys } from 'entities/permission-group';
import { useRegistryUsers, registryKeys } from 'entities/registry';
import { documentPermissionKeys } from 'entities/document-permission';
import { useQueryClient } from '@tanstack/react-query';

interface PermissionGroupOption {
  title: string;
  permission: string;
}

type PermissionUser = {
  id: number;
  firstName?: string;
  lastName?: string;
  userName?: string;
  [key: string]: unknown;
};

type UserDocumentPermission = {
  userId: number;
  permission: string;
  [key: string]: unknown;
};

type DocumentPermissionsData = {
  permissionGroups?: unknown[];
  userDocumentPermissions?: UserDocumentPermission[];
};

interface NewUserFormProps {
  permissionGroup: PermissionGroupOption;
  documentId: string;
  handleUserAdded: () => void;
}

interface UserToRemove {
  userId: number;
  userName?: string;
  permission: string;
}

const NewUserForm = ({ permissionGroup, documentId, handleUserAdded }: NewUserFormProps) => {
  const { t } = useTranslation('document-permissions');

  const [userName, setUserName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: addUser } = useAddDocumentPermissionUser();
  const { selectedOrganization } = useUser();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleAddUser = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userName) {
      return;
    }

    addUser(
      { documentId, permission: permissionGroup.permission, userName, organization: selectedOrganization },
      {
        onSuccess: ({ permission }: { user: PermissionUser; permission: string }) => {
          handleUserAdded();
          setUserName('');
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'success',
            title: t('add-user.success.title'),
            text: `${t('add-user.success.text').replace('$1', userName)} ${i18nDynamic(
              'document-permissions.permission.' + permission.toLowerCase().replace('db', 'reference-data')
            )}`,
          });
        },
      }
    );
  };

  return (
    <div className="add-new-popup wider-popup popup-with-button">
      <div className="comment--response">
        <form onSubmit={handleAddUser}>
          <input
            className="input-variant wider-input"
            type="text"
            name="username"
            value={userName}
            ref={inputRef}
            placeholder={t('field.placeholder.new-user')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setUserName(event.target.value)}
          />
          <button type="submit" className="button btn btn-secondary">
            {t('button.new-user')} {userName}
          </button>
        </form>
      </div>
    </div>
  );
};

const DocumentPermissions = () => {
  const { t } = useTranslation(['common', 'document-permissions']);

  const { documentId } = useDocumentParams();
  const queryClient = useQueryClient();
  const { data: permissions = { permissionGroups: [] } } = useDocumentPermissions(documentId) as { data?: DocumentPermissionsData };
  const { data: users = [] } = useRegistryUsers() as { data?: PermissionUser[] };
  const { mutate: removeUser } = useRemoveDocumentPermissionUser();
  const [showModal, setShowModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState<UserToRemove | null>(null);
  const menuItemsEditDocumentGroupUser = (permission: string, user: PermissionUser) => [
    {
      text: t('document-permissions:list.menu-item.remove-user'),
      icon: 'fa-regular fa-trash-can',
      onClick: () => {
        setShowModal(true);
        setUserToRemove({ userId: user.id, userName: user.userName, permission: permission });
      },
    },
  ];

  //TODO Maybe fetch this list from backend, gaby-documents or client-config
  const AVAILABLE_DOCUMENT_PERMISSIONS: PermissionGroupOption[] = [
    { title: t('document-permissions:permission.project-manager'), permission: 'ADMIN' },
    { title: t('document-permissions:permission.design'), permission: 'DESIGN' },
    { title: t('document-permissions:permission.coder'), permission: 'CODER' },
    { title: t('document-permissions:permission.reference-data'), permission: 'DB' },
  ];

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: [...permissionGroupKeys.documentPermissionsAll],
    });
    queryClient.invalidateQueries({
      queryKey: [...registryKeys.usersAll],
    });
    queryClient.invalidateQueries({
      queryKey: documentPermissionKeys.forDocument(documentId),
    });
  };

  const removeUserPermission = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userToRemove) {
      return;
    }
    removeUser(
      { documentId, permission: userToRemove.permission, userId: userToRemove.userId },
      {
        onSuccess: () => {
          invalidateAll();
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'success',
            title: t('document-permissions:remove-user.success.title'),
            text: `${t('document-permissions:remove-user.success.text').replace('$1', userToRemove.userName ?? '')} ${i18nDynamic(
              'document-permissions.permission.' + userToRemove.permission.toLowerCase().replace('db', 'reference-data')
            )}`,
          });
        },
        onError: () => {
          invalidateAll();
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'error',
            title: 'Något gick fel',
            text: `Användaren ${userToRemove.userName} har inte kunnat tas bort ${i18nDynamic(
              'document-permissions.permission.' + userToRemove.permission.toLowerCase()
            )}`,
          });
        },
      }
    );
    setShowModal(false);
  };

  const handleUserAdded = () => {
    invalidateAll();
  };

  return (
    <div>
      {AVAILABLE_DOCUMENT_PERMISSIONS.map((permission) => (
        <div key={permission.title}>
          <div className="permission-section-header columns col-1fr-auto">
            <h2 className="permission-title">{permission.title}</h2>
            <LazyTippy
              interactive={true}
              trigger="click"
              offset={[20, 10]}
              placement="bottom"
              content={<NewUserForm handleUserAdded={handleUserAdded} permissionGroup={permission} documentId={documentId} />}
            >
              <button className="fab" type="button">
                <i className="fa-regular fa-plus font-15" />
              </button>
            </LazyTippy>
          </div>
          <div>
            <table className="permissions-table">
              <tbody>
                {!permissions.userDocumentPermissions
                  ? null
                  : permissions.userDocumentPermissions
                      .filter((userDocumentPermission: UserDocumentPermission) => userDocumentPermission.permission === permission.permission)
                      .map((member: UserDocumentPermission) => {
                        const user = users.find((user) => user.id === member.userId);
                        if (!user) {
                          return (
                            <tr key={permission.permission + member.userId}>
                              <td>{t('common:loading-message')}</td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={user.id}>
                            <td className="columns col-1fr-auto" style={{ cursor: 'default' }}>
                              {user.firstName} {user.lastName}
                              <Tippy
                                interactive={true}
                                offset={[90, -80]}
                                trigger="click"
                                content={<ToolMenu menuItems={menuItemsEditDocumentGroupUser(permission.permission, user)} />}
                              >
                                <i className="fa-regular fa-ellipsis-v more toggle-icon edit-permission-group" />
                              </Tippy>
                            </td>
                          </tr>
                        );
                      })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Modal titleLabelKey={'document-permissions:remove-user.modal.title'} onClose={() => setShowModal(false)} show={showModal}>
        <form className="form" onSubmit={removeUserPermission}>
          <div>
            <span className="flex flex-row gap-1">
              <i className="fa-regular fa-trash-can modal-icon" />
              <h3 className="modal-body-title">{t('document-permissions:remove-user.modal.title')}</h3>
            </span>
            <p className="modal-body-text">
              {t('document-permissions:remove-user.modal.text')} {userToRemove?.userName} ?
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="button clear"
              onClick={() => {
                setShowModal(false);
              }}
            >
              {t('common:button.cancel')}
            </button>
            <button type="submit" className="button primary">
              {t('document-permissions:remove-user.modal.button.remove')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DocumentPermissions;
